import type {
  EquipmentDomain,
  EquipmentKind,
  EquipmentLevel,
  EquipmentLifecycleStatus,
  EquipmentProduct,
  EquipmentTechnology,
} from "../../types/equipmentLibrary";

/**
 * Minimal, import-neutral schema for the curated OEM seed.
 *
 * This intentionally does not compete with the application-wide equipment
 * library types.  Consumers should use `mapCuratedOemSeed` to adapt these
 * evidence records into the canonical runtime schema.
 */
export type EvidenceTier =
  | "tier-1-oem-primary"
  | "tier-2-regulatory-registry"
  | "tier-3-government-or-lab";

export type CatalogStatus =
  | "active"
  | "precommercial"
  | "reference-only";

export type CommercialMaturity =
  | "commercial-operating"
  | "commercially-available"
  | "demonstration"
  | "precommercial"
  | "legacy-supported";

export interface EquipmentClassification {
  readonly level: EquipmentLevel;
  readonly domain: EquipmentDomain;
  readonly kind: EquipmentKind;
  readonly technologies: readonly EquipmentTechnology[];
  readonly energySources: readonly string[];
  readonly applications: readonly string[];
}

export interface CuratedOemSeedRecord {
  readonly id: string;
  readonly manufacturer: string;
  readonly family: string;
  readonly classification: EquipmentClassification;
  readonly catalogStatus: CatalogStatus;
  readonly lifecycleStatus: EquipmentLifecycleStatus;
  readonly commercialMaturity: CommercialMaturity;
  readonly evidenceTier: EvidenceTier;
  readonly sourceIds: readonly string[];
  readonly sourceUrls: readonly string[];
  /**
   * Populated only when the cited source explicitly establishes geography.
   * An empty array is an intentional unknown, not a claim of global supply.
   */
  readonly regions: readonly string[];
  /**
   * Populated only when the cited source explicitly identifies the grid
   * frequency. Empty means "not yet normalized from primary evidence".
   */
  readonly frequencyHz: readonly (50 | 60)[];
  readonly dataGaps: readonly string[];
}

export type SourceKind =
  | "oem-product-page"
  | "oem-portfolio-page"
  | "oem-datasheet"
  | "oem-brochure"
  | "regulatory-registry"
  | "government-dataset"
  | "open-source-model";

export type RightsPosture =
  | "facts-only-no-document-redistribution"
  | "public-domain"
  | "open-source"
  | "terms-review-required";

export interface EquipmentEvidenceSource {
  readonly id: string;
  readonly publisher: string;
  readonly title: string;
  readonly url: string;
  readonly kind: SourceKind;
  readonly evidenceTier: EvidenceTier;
  readonly lastVerifiedOn: string;
  readonly nextReviewOn: string;
  readonly rights: {
    readonly posture: RightsPosture;
    readonly licenseOrTerms: string;
    readonly allowedUse: string;
    readonly prohibitedUse: string;
  };
  readonly notes?: string;
}

export interface SeedCoverageSummary {
  readonly total: number;
  readonly manufacturers: number;
  readonly categories: Readonly<Record<string, number>>;
  readonly domains: Readonly<Record<string, number>>;
  readonly statuses: Readonly<Record<string, number>>;
  readonly evidenceTiers: Readonly<Record<string, number>>;
  readonly recordsWithRegionEvidence: number;
  readonly recordsWithFrequencyEvidence: number;
}

/**
 * Digest-free staging payload accepted by the canonical ingestion layer.
 * Digests and timestamps are assigned only after review and canonicalization.
 */
export type CanonicalEquipmentProductDraft = Omit<
  EquipmentProduct,
  | "schemaVersion"
  | "digestAlgorithm"
  | "contentDigest"
  | "createdAt"
  | "updatedAt"
  | "currentRevisionId"
>;

/**
 * Pure adapter for mapping the evidence seed into any canonical application
 * representation without coupling this curated dataset to a changing schema.
 */
export function mapCuratedOemSeed<T>(
  records: readonly CuratedOemSeedRecord[],
  adapter: (record: CuratedOemSeedRecord) => T,
): readonly T[] {
  return records.map(adapter);
}

export function toCanonicalProductDraft(
  record: CuratedOemSeedRecord,
): CanonicalEquipmentProductDraft {
  return {
    id: record.id,
    level: record.classification.level,
    domain: record.classification.domain,
    kind: record.classification.kind,
    manufacturer: record.manufacturer,
    family: record.family,
    model: record.family,
    displayName: `${record.manufacturer} ${record.family}`,
    lifecycleStatus: record.lifecycleStatus,
    tags: [
      "curated-oem-seed",
      record.evidenceTier,
      record.catalogStatus,
    ],
    facets: {
      technologies: record.classification.technologies,
      energySources: record.classification.energySources,
      fuels: [],
      primeMovers: [],
      cycles: [],
      coolingMethods: [],
      gridRoles: [],
      applications: record.classification.applications,
      regions: record.regions,
    },
    visibility: "platform",
    origin: "oem-publication",
  };
}
